export interface TechStack {
  id: string;
  name: string;
  category: 'language' | 'frontend' | 'backend' | 'database' | 'cloud' | 'devops' | 'mobile' | 'testing' | 'desktop' | 'other';
  description?: string;
  color: string;
  icon?: string;
  website?: string;
}

export type TechCategory = 
  | 'language'
  | 'frontend'
  | 'backend'
  | 'database'
  | 'cloud'
  | 'mobile'
  | 'desktop'
  | 'devops';

export const techCategories: Record<TechCategory, string> = {
  language: '🧠 Langages de programmation',
  frontend: '🎨 Front-end (UI / Web)',
  backend: '⚙️ Back-end (serveurs/API)',
  database: '🗄️ Bases de données',
  cloud: '☁️ Backend-as-a-Service / Cloud',
  mobile: '📱 Mobile / Cross-platform',
  desktop: '🖥️ Desktop',
  devops: '🧪 Outils & DevOps'
};

export const techStacks: TechStack[] = [
  // 🧠 Langages de programmation
  {
    id: 'js',
    name: 'JavaScript',
    category: 'language',
    description: 'Un langage de programmation léger, interprété et orienté objet avec des fonctions de première classe',
    color: '#F7DF1E',
    website: 'https://developer.mozilla.org/fr/docs/Web/JavaScript'
  },
  {
    id: 'ts',
    name: 'TypeScript',
    category: 'language',
    description: 'Un sur-ensemble typé de JavaScript qui se compile en JavaScript simple',
    color: '#3178C6',
    website: 'https://www.typescriptlang.org/'
  },
  {
    id: 'python',
    name: 'Python',
    category: 'language',
    description: 'Un langage de programmation interprété, orienté objet et de haut niveau',
    color: '#3776AB',
    website: 'https://www.python.org/'
  },
  {
    id: 'java',
    name: 'Java',
    category: 'language',
    description: 'Un langage de programmation orienté objet, compilé et fortement typé',
    color: '#007396',
    website: 'https://www.java.com/'
  },
  {
    id: 'csharp',
    name: 'C#',
    category: 'language',
    description: 'Un langage de programmation polyvalent, orienté objet et fortement typé',
    color: '#239120',
    website: 'https://docs.microsoft.com/fr-fr/dotnet/csharp/'
  },
  {
    id: 'php',
    name: 'PHP',
    category: 'language',
    description: 'Un langage de script conçu pour le développement web',
    color: '#777BB4',
    website: 'https://www.php.net/'
  },

  // 🎨 Front-end (UI / Web)
  { id: 'react', name: 'React', category: 'frontend', color: '#61DAFB' },
  { id: 'vue', name: 'Vue.js', category: 'frontend', color: '#4FC08D' },
  { id: 'angular', name: 'Angular', category: 'frontend', color: '#DD0031' },
  { id: 'svelte', name: 'Svelte', category: 'frontend', color: '#FF3E00' },
  { id: 'nextjs', name: 'Next.js', category: 'frontend', color: '#000000' },
  { id: 'tailwind', name: 'Tailwind CSS', category: 'frontend', color: '#38B2AC' },
  { id: 'bootstrap', name: 'Bootstrap', category: 'frontend', color: '#7952B3' },

  // ⚙️ Back-end (serveurs/API)
  { id: 'nodejs', name: 'Node.js', category: 'backend', color: '#339933' },
  { id: 'express', name: 'Express.js', category: 'backend', color: '#000000' },
  { id: 'nestjs', name: 'NestJS', category: 'backend', color: '#E0234E' },
  { id: 'django', name: 'Django', category: 'backend', color: '#092E20' },
  { id: 'flask', name: 'Flask', category: 'backend', color: '#000000' },
  { id: 'fastapi', name: 'FastAPI', category: 'backend', color: '#009688' },
  { id: 'dotnet', name: '.NET Core / ASP.NET', category: 'backend', color: '#512BD4' },
  { id: 'spring', name: 'Spring Boot', category: 'backend', color: '#6DB33F' },

  // 🗄️ Bases de données
  { id: 'postgresql', name: 'PostgreSQL', category: 'database', color: '#336791' },
  { id: 'mysql', name: 'MySQL / MariaDB', category: 'database', color: '#4479A1' },
  { id: 'mongodb', name: 'MongoDB', category: 'database', color: '#47A248' },
  { id: 'sqlite', name: 'SQLite', category: 'database', color: '#003B57' },
  { id: 'firestore', name: 'Firebase Firestore', category: 'database', color: '#FFCA28' },
  { id: 'realtime-db', name: 'Realtime Database', category: 'database', color: '#FFCA28' },
  { id: 'supabase-db', name: 'Supabase', category: 'database', color: '#3ECF8E' },
  { id: 'planetscale', name: 'PlanetScale', category: 'database', color: '#000000' },
  { id: 'redis', name: 'Redis', category: 'database', color: '#DC382D' },
  { id: 'dynamodb', name: 'DynamoDB', category: 'database', color: '#4053D6' },

  // ☁️ Backend-as-a-Service / Cloud
  { id: 'firebase', name: 'Firebase', category: 'cloud', color: '#FFCA28' },
  { id: 'supabase', name: 'Supabase', category: 'cloud', color: '#3ECF8E' },
  { id: 'aws', name: 'AWS Amplify / Lambda', category: 'cloud', color: '#FF9900' },
  { id: 'gcp', name: 'Google Cloud Platform', category: 'cloud', color: '#4285F4' },
  { id: 'azure', name: 'Azure', category: 'cloud', color: '#0078D4' },
  { id: 'heroku', name: 'Heroku', category: 'cloud', color: '#430098' },
  { id: 'railway', name: 'Railway / Render / Vercel / Netlify', category: 'cloud', color: '#0B0D0E' },

  // 📱 Mobile / Cross-platform
  { id: 'react-native', name: 'React Native', category: 'mobile', color: '#61DAFB' },
  { id: 'flutter', name: 'Flutter', category: 'mobile', color: '#02569B' },
  { id: 'swiftui', name: 'Swift / SwiftUI', category: 'mobile', color: '#FA7343' },
  { id: 'jetpack', name: 'Kotlin / Jetpack Compose', category: 'mobile', color: '#7F52FF' },
  { id: 'ionic', name: 'Capacitor / Ionic', category: 'mobile', color: '#3880FF' },
  { id: 'unity', name: 'Unity', category: 'mobile', color: '#000000' },

  // 🖥️ Desktop
  { id: 'electron', name: 'Electron', category: 'desktop', color: '#47848F' },
  { id: 'tauri', name: 'Tauri', category: 'desktop', color: '#FFC131' },

  // 🧪 Outils & DevOps
  { id: 'docker', name: 'Docker', category: 'devops', color: '#2496ED' },
  { id: 'github-actions', name: 'GitHub Actions / GitLab CI/CD', category: 'devops', color: '#2088FF' },
  { id: 'vercel', name: 'Vercel / Netlify', category: 'devops', color: '#000000' },
  { id: 'firebase-hosting', name: 'Firebase Hosting', category: 'devops', color: '#FFCA28' },
  { id: 'pm2', name: 'PM2', category: 'devops', color: '#2B037A' },
  { id: 'nginx', name: 'Nginx / Apache', category: 'devops', color: '#009639' },

  // Tests
  {
    id: 'jest',
    name: 'Jest',
    category: 'testing',
    description: 'Un framework de test JavaScript délicieux mettant l\'accent sur la simplicité',
    color: '#C21325',
    website: 'https://jestjs.io/'
  },
  {
    id: 'cypress',
    name: 'Cypress',
    category: 'testing',
    description: 'Un framework de test de bout en bout rapide, facile et fiable pour tout ce qui fonctionne dans un navigateur',
    color: '#17202C',
    website: 'https://www.cypress.io/'
  },
  {
    id: 'selenium',
    name: 'Selenium',
    category: 'testing',
    description: 'Un outil d\'automatisation de navigateur pour tester des applications web',
    color: '#43B02A',
    website: 'https://www.selenium.dev/'
  }
];

export const getTechStacksByCategory = (category: TechCategory): TechStack[] => {
  return techStacks.filter(stack => stack.category === category);
};

export const getTechStackById = (id: string): TechStack | undefined => {
  return techStacks.find(stack => stack.id === id);
};

export const getAllTechStacks = (): TechStack[] => {
  return [...techStacks];
};

export const searchTechStacks = (query: string): TechStack[] => {
  const lowerCaseQuery = query.toLowerCase();
  return techStacks.filter(stack => 
    stack.name.toLowerCase().includes(lowerCaseQuery) || 
    (stack.description && stack.description.toLowerCase().includes(lowerCaseQuery))
  );
}; 